// Entry point for the build script in your package.json
import "@hotwired/turbo-rails"
import "./controllers"
// The few heavy pages are never fetched on hover, and a Placements panel
// that cannot load says so (2026-10-17).
import "./controllers/heavy_pages"
import "./controllers/placement_panel_frames"

// M7 (PRT absorption): PRT's importmap pinned @rails/activestorage and
// started it in its entrypoint (User avatars + DPK/track direct uploads;
// press_clip_editor_controller imports DirectUpload directly). Ported into
// the esbuild bundle — PRT's only other pins (turbo/stimulus/
// stimulus-loading/actioncable) were already here, and PRT vendored no JS.
import * as ActiveStorage from "@rails/activestorage"
ActiveStorage.start()

// Override Turbo's browser confirm() with a styled BMT modal
import * as Turbo from "@hotwired/turbo"

// M5 (BFT absorption): BFT's window.appConfirm, ported verbatim from
// trend-bft/app/javascript/application.js. BFT department views call
// appConfirm() directly (with custom labels) and the BFT layout ships the
// backing #turbo-confirm-dialog element. On pages that carry that element
// (the /bf layout), Turbo confirms route through it, preserving BFT's
// dialog; everywhere else the BMT modal below serves.
window.appConfirm = function(message, options) {
  const dialog = document.getElementById("turbo-confirm-dialog")
  // No BFT dialog on this page: serve the BMT-styled modal below instead.
  // (This used to fall back to the browser confirm(), which the native-
  // modal house rule bans; owner directive 2026-08-13.)
  if (!dialog) return buildConfirmModal(message, options)

  const messageEl = dialog.querySelector("[data-confirm-message]")
  const confirmBtn = dialog.querySelector("[data-confirm-accept]")
  const cancelBtn = dialog.querySelector("[data-confirm-cancel]")

  const opts = options || {}
  messageEl.textContent = message
  confirmBtn.textContent = opts.confirmLabel || "Confirm"
  cancelBtn.textContent = opts.cancelLabel || "Cancel"
  // Set or clear on EVERY call: the dialog is reused, and a title left
  // from the last confirm must never head the next one.
  const titleEl = dialog.querySelector("[data-confirm-title]")
  if (titleEl) {
    titleEl.textContent = opts.title || ""
    titleEl.hidden = !opts.title
  }
  dialog.classList.remove("hidden")
  // A send confirm focuses Cancel: Enter from a textarea must never fall
  // through onto Send (releases register ruling, 2026-09-02).
  ;(opts.focus === "cancel" ? cancelBtn : confirmBtn).focus()

  return new Promise((resolve) => {
    const cleanup = () => {
      dialog.classList.add("hidden")
      confirmBtn.removeEventListener("click", onConfirm)
      cancelBtn.removeEventListener("click", onCancel)
      backdrop.removeEventListener("click", onCancel)
      document.removeEventListener("keydown", onKeydown)
    }

    const onConfirm = () => { cleanup(); resolve(true) }
    const onCancel = () => { cleanup(); resolve(false) }
    const onKeydown = (e) => { if (e.key === "Escape") onCancel() }

    const backdrop = dialog.querySelector("[data-confirm-backdrop]")
    confirmBtn.addEventListener("click", onConfirm)
    cancelBtn.addEventListener("click", onCancel)
    backdrop.addEventListener("click", onCancel)
    document.addEventListener("keydown", onKeydown)
  })
}

// The BMT-styled confirm modal: the ONE fallback wherever the BFT dialog
// element is absent, shared by turbo_confirm and direct appConfirm callers
// (the Stimulus flows on turbo:false forms). Options: confirmLabel,
// cancelLabel, title (replaces "Confirm Action" and hides the warning
// icon: a question like "Send to 3 people?" is not a warning), focus
// ("cancel" | "confirm", default confirm).
const buildConfirmModal = (message, options) => {
  return new Promise((resolve) => {
    // .app-modal-* classes live in uds-support.css (token-based, loaded by
    // every shell): the old Tailwind strings rendered UNSTYLED on the
    // Tailwind-less SPT pages (Discovery Line commit D prerequisite).
    const backdrop = document.createElement("div")
    backdrop.className = "app-modal-backdrop"
    backdrop.style.cssText = "animation:fadeIn .15s ease-out"

    const modal = document.createElement("div")
    modal.className = "app-modal"
    modal.style.animation = "scaleIn .15s ease-out"

    modal.innerHTML = `
      <div class="app-modal-body">
        <div class="app-modal-icon">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/>
          </svg>
        </div>
        <h3 class="app-modal-title">Confirm Action</h3>
        <p class="app-modal-message" data-role="message"></p>
      </div>
      <div class="app-modal-actions">
        <button data-role="cancel" class="app-modal-btn app-modal-btn-cancel">
          Cancel
        </button>
        <button data-role="confirm" class="app-modal-btn app-modal-btn-confirm">
          Confirm
        </button>
      </div>
    `

    // textContent, never template interpolation: confirm messages can carry
    // data a portal client typed (their own email), and innerHTML would run it.
    modal.querySelector('[data-role="message"]').textContent = message
    const opts = options || {}
    if (opts.confirmLabel) modal.querySelector('[data-role="confirm"]').textContent = opts.confirmLabel
    if (opts.cancelLabel) modal.querySelector('[data-role="cancel"]').textContent = opts.cancelLabel
    if (opts.title) {
      modal.querySelector(".app-modal-title").textContent = opts.title
      modal.querySelector(".app-modal-icon").hidden = true
    }
    // A recipient list above the message ([{initials, name, detail}]),
    // built with createElement: names are data, never markup.
    if (Array.isArray(opts.items) && opts.items.length) {
      const ul = document.createElement("ul")
      ul.className = "app-modal-list"
      for (const item of opts.items) {
        const li = document.createElement("li")
        const av = document.createElement("span"); av.className = "app-modal-list-avatar"; av.textContent = item.initials || "?"
        const nm = document.createElement("span"); nm.className = "app-modal-list-name"; nm.textContent = item.name || ""
        const dt = document.createElement("span"); dt.className = "app-modal-list-detail"; dt.textContent = item.detail || ""
        li.append(av, nm, dt); ul.appendChild(li)
      }
      const msg = modal.querySelector('[data-role="message"]')
      msg.parentNode.insertBefore(ul, msg)
    }

    backdrop.appendChild(modal)
    document.body.appendChild(backdrop)

    const cleanup = (result) => {
      backdrop.remove()
      resolve(result)
    }

    backdrop.querySelector("[data-role='cancel']").addEventListener("click", () => cleanup(false))
    backdrop.querySelector("[data-role='confirm']").addEventListener("click", () => cleanup(true))
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) cleanup(false) })
    document.addEventListener("keydown", function handler(e) {
      if (e.key === "Escape") { document.removeEventListener("keydown", handler); cleanup(false) }
    })
    backdrop.querySelector(opts.focus === "cancel" ? "[data-role='cancel']" : "[data-role='confirm']").focus()
  })
}

// Turbo confirms route through the same pair: the BFT dialog where its
// layout ships it, the BMT modal everywhere else.
Turbo.setConfirmMethod((message) => window.appConfirm(message))

// M6 (SPT absorption): SPT's ActionCable consumer, ported verbatim from
// trend-spt/app/javascript/application.js. The SPT activity-bell Stimulus
// controller subscribes to ActivityLogChannel through window.App.cable;
// creating the consumer is lazy (no socket until a subscription is made),
// so non-SPT pages are unaffected.
import { createConsumer } from "@rails/actioncable"
window.App = window.App || {}
window.App.cable = createConsumer()

// Service worker: push notifications only, no caching. Guarded so a browser
// without it runs the app exactly as before; a failed registration is
// logged, never thrown. This module runs once per full page load, which is
// all a registration needs (Turbo visits do not re-run it, and the browser
// re-checks the worker file on its own).
if ("serviceWorker" in navigator && document.querySelector('link[rel="manifest"]')) {
  navigator.serviceWorker.register("/service-worker", { scope: "/" })
    .catch((error) => console.warn("Service worker registration failed:", error))
}
